import { IController, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';

import { CreditRequirements } from '../../../../../ng2/shared/constants/credit-requirements.constant';
import { ISdcStore } from '../../../flux/sdc-store.interface';

interface IGraduationPlanByHumanName {
  [humanName: string]: {
    outcomeCategory: string;
    humanName: string;
  };
}

export class CreditGapsWizardSubjectTableCtrl implements IController {
  static inject: string[] = [
    '$scope',
    '$state',
    '$window',
    'SDCStore',
    'ObjectCache',
    'StudentListPreviewModal',
    'ImStudent',
  ];

  public creditReqs;
  public orderedCreditReqs;
  public currentStudents;
  public columnKeys: string[];
  public table;
  public tableRows;
  public total;
  public studentFieldForTableRows: string;
  public uiReady = false;
  public filterOptions;
  public currentFilter;

  constructor(
    private $scope,
    private $state: IStateService,
    private $window: IWindowService,
    private SDCStore: ISdcStore,
    private ObjectCache: any,
    public StudentListPreviewModal: any,
    private ImStudent,
  ) {
    this.filterOptions = {
      columnKeys: ['gradPlan', 'creditMaxGaps', 'creditMaxGapsNoPlan'],
      sort: {
        colId: 'gradPlan',
        direction: 'desc',
      },
    };

    this.tableRows = {
      unaddressedGaps: {},
      addressedGaps: {},
      noGaps: {},
    };
  }

  get filteredReqs() {
    const filter = _.filter(CreditRequirements, (req: any) => {
      if (req.orders.creditGapsWizard) return req;
    });
    return filter;
  }

  $onInit(): void {
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUi();
        this.setListeners();
        this.uiReady = true;
      }
    });
  }

  private setListeners() {
    this.$scope.$listenTo(this.SDCStore, ['filteredFlattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['filter'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['flattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['school'], this.setUi.bind(this));
  }

  private getOrderedCreditRequirements() {
    return _.sortBy(this.filteredReqs, requirement => {
      return requirement.orders.creditGapsWizard;
    });
  }

  public setUi() {
    const currentCohortFilter = this.SDCStore.getFilter().cohort;
    this.currentFilter = this.getCurrentCohort(currentCohortFilter);
    const studentFilter = currentCohortFilter ? { status: ['A'], cohort: currentCohortFilter } : { status: ['A'] };
    this.currentStudents = this.SDCStore.getFilteredFlattenedStudents(studentFilter);

    // creates table shell
    this.orderedCreditReqs = this.getOrderedCreditRequirements();
    _.each(this.orderedCreditReqs, (requirement: any) => {
      this.tableRows.unaddressedGaps[requirement.camelCase] = { students: [] };
      this.tableRows.addressedGaps[requirement.camelCase] = { students: [] };
      this.tableRows.noGaps[requirement.camelCase] = { students: [] };
    });
    this.table = this.populateTable(this.orderedCreditReqs);
  }

  public populateTable(requirements): any {
    return _.reduce(
      this.currentStudents,
      (acc: any, student: any) => {
        const { studentId } = student;
        let colKey;
        let creditGapsStartValue;
        let creditGapsValue;

        // for each student, check gap status for each requirement
        _.each(requirements, (req: any) => {
          colKey = req.camelCase;
          // find two columns on each student that map to the current credit requirement
          const creditReq = _.upperFirst(colKey).toString();
          creditGapsStartValue = student[`creditGapsStart${creditReq}`];
          creditGapsValue = student[`creditGaps${creditReq}`];

          const studentHasUnaddressedGaps = creditGapsValue < 0;
          const studentHasAddressedGaps = creditGapsValue >= 0 && creditGapsStartValue < 0;
          const studentHasNoGaps = creditGapsStartValue >= 0;

          if (studentHasUnaddressedGaps) {
            acc.unaddressedGaps[colKey].students.push(studentId);
          } else if (studentHasAddressedGaps) {
            acc.addressedGaps[colKey].students.push(studentId);
          } else if (studentHasNoGaps) {
            acc.noGaps[colKey].students.push(studentId);
          }
        });
        return acc;
      },
      this.tableRows,
    );
  }

  public getCurrentCohort(currentCohortFilter) {
    return _.last(currentCohortFilter);
  }
}
